/**
 * Offshore Sails Login Page — OS-INV-04
 *
 * Reuses the SHARED Firebase auth flow (useAuth) exactly like MadebbLogin —
 * no new auth system. Validates against the 'offshore-sails' app authorization
 * and, on success, routes to '/os/inventory' (the default OS dashboard view).
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/** Post-login destination (AC: cleanest OS route — Inventory is the primary view). */
const POST_LOGIN_ROUTE = '/os/inventory';

export const OffshoreLogin: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithEmail, error: authError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle('offshore-sails');
      navigate(POST_LOGIN_ROUTE);
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmail(email, password, 'offshore-sails');
      navigate(POST_LOGIN_ROUTE);
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #002680 0%, #001550 50%, #000a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={8} sx={{ padding: 4, borderRadius: 4, background: 'white' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
            <img
              src="/os/Offshore Sails-Horizontal-MainColor@4x-8.png"
              alt="Offshore Sails"
              style={{ maxWidth: '320px', width: '100%', height: 'auto' }}
            />
          </Box>

          <Typography
            variant="h5"
            component="h1"
            sx={{ textAlign: 'center', marginBottom: 3, fontWeight: 600, color: '#001550' }}
          >
            Staff Login
          </Typography>

          {(error || authError) && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error || authError}
            </Alert>
          )}

          <form onSubmit={handleEmailSignIn}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ 'data-testid': 'os-login-email' }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              inputProps={{ 'data-testid': 'os-login-password' }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                marginTop: 3,
                marginBottom: 2,
                padding: '12px',
                background: '#FFC726',
                color: '#001550',
                fontWeight: 700,
                '&:hover': { background: '#ffd454' },
              }}
              data-testid="os-login-submit"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ marginY: 2 }}>OR</Divider>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              padding: '12px',
              borderColor: '#002680',
              color: '#002680',
              '&:hover': { borderColor: '#001550', background: 'rgba(0, 38, 128, 0.04)' },
            }}
            data-testid="os-login-google"
          >
            Sign in with Google
          </Button>

          <Box sx={{ textAlign: 'center', marginTop: 3 }}>
            <Button variant="text" onClick={() => navigate('/os')} sx={{ color: '#666' }}>
              Back to Landing Page
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
